import {
    compact,
    filter,
    includes,
    invoke,
    isEqual,
    isNil,
    mergeWith,
} from "lodash";
import { useRouter } from "next/router";
import {
    DataTableFilterMeta,
    DataTable as PrimeReactDataTable,
} from "primereact/datatable";
import { PropsWithChildren, forwardRef, useEffect, useId, useRef } from "react";
import { twMerge } from "tailwind-merge";
import {
    IconBetterspace,
    IconSort,
    IconSortProps,
} from "../../../features/layout/components/Icons";
import {
    PAGINATOR_PAGES_CLASSNAME,
    TABLE_WRAPPER_CLASSNAME,
} from "../../constants";
import { useTableStore } from "../../store/useTableStore";
import { useUIStore } from "../../store/useUIStore";

export type ColumnSelectionModes = "multiple" | "single" | undefined;

interface DataTableProps extends PropsWithChildren {
    id?: string;
    value: any[];
    key?: string | null;
    rows?: number;
    onValueChange?: (value: any[]) => void;
    filters?: DataTableFilterMeta;
}

// Merge both classNames if they have the same key
const mergeClassNames = (objVal: any, srcVal: any, key: string) => {
    if (
        key === "className" &&
        typeof objVal === "string" &&
        typeof srcVal === "string"
    ) {
        return twMerge(objVal, srcVal);
    }
};

const withDefaultProps = <P extends object>(
    Component: React.ComponentType<P>
) => {
    const WithDefaultPropsComponent = forwardRef<any, P & DataTableProps>(
        function WithDefaultProps(
            {
                id: existingId,
                value,
                onValueChange,
                filters,
                children,
                ...restProps
            },
            ref
        ) {
            const fallbackId = `data-table-${useId()}`;
            const id = existingId || fallbackId;

            const { theme } = useUIStore();

            const prevValueRef = useRef<any[]>(value);

            // Custom onValueChange that triggers only if value actually changed, otherwise is does not work with our header dropdown sort
            useEffect(() => {
                if (!isEqual(prevValueRef.current, value)) {
                    onValueChange?.(value);
                    prevValueRef.current = value;
                }
            }, [value, onValueChange]);

            const defaultProps = {
                id,
                sortIcon: (props: IconSortProps) => <IconSort {...props} />,
                pt: {
                    wrapper: {
                        className: TABLE_WRAPPER_CLASSNAME,
                    },
                    header: {
                        className: "border-none bg-transparent px-0",
                    },
                    headerRow: {
                        className:
                            "first:uppercase *:font-medium [&_.p-highlight]:bg-primary-500/10 [&_.p-highlight:hover]:bg-primary-500/20",
                    },
                    column: {
                        headerCell: {
                            className:
                                "h-10.5 bg-surface-520! px-2.5! py-0! text-xxs! font-medium! leading-4! text-text-400! border-b-0! [&.p-sortable-column:hover]:bg-surface-530! [&.p-sortable-column.p-highlight:not(:hover)]:bg-primary-50! [&.p-sortable-column.p-highlight:hover]:bg-primary-80! [&.p-sortable-column.p-highlight]:text-primary-800! [&.p-sortable-column.p-highlight]:font-semibold! transition-colors duration-[120ms] ease-in-out relative before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-px before:bg-surface-555 first:before:hidden",
                        },
                    },
                    tbody: {
                        className:
                            "[&_tr.p-highlight]:bg-surface-530! [&_td]:py-2 [&_td]:px-2.5! [&_td>*]:animate-[fade-in_0.3s] [&_td]:border-surface-555! [&_tr:not(.p-highlight):hover]:bg-surface-510! [&_tr]:transition-colors [&_tr]:duration-[120ms] [&_tr]:ease-in-out [&_tr:last-child_td]:border-b-0!",
                    },
                    paginator: {
                        root: {
                            className: "px-0!",
                        },
                        pages: {
                            className: PAGINATOR_PAGES_CLASSNAME,
                        },
                        left: {
                            className: "mr-0! md:mr-auto!",
                        },
                    },
                },
                // Fix drag selection, see https://github.com/primefaces/primereact/issues/7321
                onMouseDownCapture: (e: MouseEvent) => {
                    // For removable chips in the table to avoid unfocusing first
                    if (invoke(e.target, "closest", ".p-chip")) {
                        return;
                    }
                    e.stopPropagation();
                },
            };

            const router = useRouter();
            const locationId = router.query.locationId as string;
            const { visibleColumns } = useTableStore(id, locationId);

            const filteredChildren = Array.isArray(children)
                ? filter(
                      compact(children),
                      (child) =>
                          (child.props.toggleable &&
                              includes(visibleColumns, child.props.field)) ||
                          !child.props.toggleable ||
                          isNil(visibleColumns)
                  )
                : children;

            return (
                <Component
                    ref={ref}
                    value={value}
                    filters={filters}
                    onValueChange={filters ? onValueChange : undefined} // Set function when filtering is done on the FE-side in order to trigger it correctly
                    {...(mergeWith(
                        {},
                        defaultProps,
                        { ...restProps, children: filteredChildren },
                        mergeClassNames
                    ) as P)}
                    loadingIcon={
                        <div className="size-14">
                            <IconBetterspace animate type={theme} />
                        </div>
                    }
                />
            );
        }
    );

    return WithDefaultPropsComponent;
};

export default withDefaultProps(PrimeReactDataTable);
